import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Box, Button, MenuItem, Select, SelectChangeEvent, Stack, useTheme } from '@mui/material';

const pageSizeOptions = [10, 25, 50, 100];

const squareBtnSx = {
  minWidth: 32,
  width: 32,
  height: 32,
  p: 0,
  borderRadius: '4px',
  border: '1px solid',
  borderColor: 'divider',
  fontSize: '0.8125rem',
  fontWeight: 600,
};

const CustomTablePaginationActions = ({ count, page, rowsPerPage, onPageChange, onRowsPerPageChange }: any) => {
  const theme = useTheme();
  const totalPages = Math.max(1, Math.ceil(count / rowsPerPage) || 1);
  const currentPage = page + 1;

  const handleBackButtonClick = () => {
    onPageChange?.(null, page - 1);
  };

  const handleNextButtonClick = () => {
    onPageChange?.(null, page + 1);
  };

  const handlePageNumberClick = (pageNum: number) => {
    onPageChange?.(null, pageNum - 1);
  };

  const handleRowsPerPageChange = (event: SelectChangeEvent<any>) => {
    const newRowsPerPage = parseInt(event.target.value as string, 10);
    onRowsPerPageChange?.(event, newRowsPerPage);
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else if (currentPage <= 3) {
      for (let i = 1; i <= 4; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1);
      pages.push('...');
      for (let i = totalPages - 3; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      pages.push('...');
      for (let i = currentPage - 1; i <= currentPage + 1; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent={{ xs: 'center', sm: 'flex-end' }}
      spacing={1.25}
      sx={{ width: { xs: '100%', sm: 'auto' }, flexWrap: 'wrap' }}>
      <Select
        data-testId="rowsPerPage"
        value={rowsPerPage}
        onChange={handleRowsPerPageChange}
        size="small"
        renderValue={value => `${value} per page`}
        sx={{
          height: 32,
          fontSize: '0.8125rem',
          bgcolor: '#fff',
          '& .MuiSelect-select': {
            py: 0.5,
            pr: '28px !important',
          },
        }}
        inputProps={{ 'aria-label': 'rows per page' }}>
        {pageSizeOptions.map(size => (
          <MenuItem key={size} value={size} sx={{ fontSize: 13 }}>
            {size} per page
          </MenuItem>
        ))}
      </Select>

      <Box display="flex" alignItems="center" gap={0.75}>
        <Button
          data-testId="before"
          onClick={handleBackButtonClick}
          disabled={page === 0}
          aria-label="previous page"
          sx={{
            ...squareBtnSx,
            color: theme.palette.text.primary,
            '&:disabled': { opacity: 0.4 },
          }}>
          <NavigateBeforeIcon fontSize="small" />
        </Button>
        {getPageNumbers().map((pageNum, index) => {
          const isActive = pageNum === currentPage;
          const isEllipsis = pageNum === '...';
          return (
            <Button
              key={`${pageNum}-${index}`}
              onClick={() => typeof pageNum === 'number' && handlePageNumberClick(pageNum)}
              disabled={isEllipsis}
              sx={{
                ...squareBtnSx,
                borderColor: isActive ? theme.palette.primary.main : theme.palette.divider,
                backgroundColor: isActive ? theme.palette.primary.main : '#fff',
                color: isActive ? theme.palette.primary.contrastText : theme.palette.text.primary,
                cursor: isEllipsis ? 'default' : 'pointer',
                '&:hover': {
                  backgroundColor: isActive ? theme.palette.primary.dark : theme.palette.action.hover,
                  borderColor: isActive ? theme.palette.primary.dark : theme.palette.divider,
                },
                '&:disabled': {
                  backgroundColor: 'transparent',
                  color: theme.palette.text.secondary,
                  borderColor: 'transparent',
                },
              }}>
              {pageNum}
            </Button>
          );
        })}
        <Button
          data-testId="next"
          onClick={handleNextButtonClick}
          disabled={page >= totalPages - 1 || count === 0}
          aria-label="next page"
          sx={{
            ...squareBtnSx,
            color: theme.palette.text.primary,
            '&:disabled': { opacity: 0.4 },
          }}>
          <NavigateNextIcon fontSize="small" />
        </Button>
      </Box>
    </Stack>
  );
};

export default CustomTablePaginationActions;
